import { Globe, Lock } from "lucide-react";
import wallpaper1 from "../../../../assets/images/domain/wallpaper1.webp";
import wallpaper2 from "../../../../assets/images/domain/wallpaper2.webp";
import wallpaper3 from "../../../../assets/images/domain/complete-control.webp";

export default function WhyWorkWithUs() {
	return (
		<section className="container py-16 px-4 sm:px-6">
			{/* Header */}
			<div className="text-center mb-16">
				<h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4 text-gray-50">
					Why Choose Our Domains?
				</h2>
				<p className="md:text-lg max-w-3xl mx-auto leading-relaxed text-gray-300">
					Simple domain registration, happy customers, tailored websites,
					professional email, and strong security — everything your business needs to grow.
				</p>
			</div>

			{/* ── ROW 1 ── */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">

				{/* Card 1 — Domain selection */}
				<div
					className="rounded-2xl p-6 flex flex-col min-h-[300px] sm:min-h-[350px] overflow-hidden relative"
					style={{
						background: "linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
						border: "1px solid rgba(255,255,255,0.08)",
						boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
					}}
				>
					<div
						className="absolute top-0 left-[20%] right-[20%] h-px"
						style={{ background: "linear-gradient(90deg, transparent, rgba(45,212,191,0.4), transparent)" }}
					/>
					<p className="text-xl sm:text-2xl font-semibold leading-snug mb-auto text-gray-100">
						A wide range of domain extensions — from .com to .xyz and more
					</p>
					<div className="relative mt-6">
						<div
							className="relative z-10 rounded-full px-5 py-2 inline-flex items-center justify-end w-40 mb-[-14px]"
							style={{
								background: "rgba(255,255,255,0.08)",
								border: "1px solid rgba(255,255,255,0.14)",
								backdropFilter: "blur(8px)",
							}}
						>
							<span className="font-semibold text-sm text-violet-400">.com</span>
						</div>
						<div className="rounded-xl overflow-hidden h-40">
							<img
								src={wallpaper1}
								alt="Domain selection"
								className="w-full h-full object-cover rounded-xl"
								style={{ filter: "brightness(0.72) saturate(0.85)" }}
							/>
						</div>
					</div>
				</div>

				{/* Card 2 — Security */}
				<div
					className="rounded-2xl p-6 flex flex-col min-h-[300px] sm:min-h-[350px] relative"
					style={{
						background: "linear-gradient(145deg, rgba(45,212,191,0.08) 0%, rgba(255,255,255,0.02) 100%)",
						border: "1px solid rgba(45,212,191,0.15)",
						boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
					}}
				>
					<div
						className="absolute top-0 left-[20%] right-[20%] h-px"
						style={{ background: "linear-gradient(90deg, transparent, rgba(45,212,191,0.6), transparent)" }}
					/>
					<p className="text-xl sm:text-2xl font-semibold leading-snug mb-auto text-gray-100">
						Advanced security that keeps your website safe — so you can relax
					</p>
					<div className="mt-6 flex items-center gap-3">
						<div
							className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
							style={{
								background: "rgba(45,212,191,0.2)",
								border: "1px solid rgba(45,212,191,0.35)",
								boxShadow: "0 0 16px rgba(45,212,191,0.2)",
							}}
						>
							<Lock className="w-5 h-5 text-teal-400" strokeWidth={2} />
						</div>
						<span className="text-2xl font-bold tracking-tight text-teal-400">
							https://
						</span>
					</div>
				</div>

				{/* Card 3 — Complete control */}
				<div
					className="rounded-2xl overflow-hidden flex flex-col min-h-[300px] sm:min-h-[350px] sm:col-span-2 lg:col-span-1 relative"
					style={{
						background: "linear-gradient(145deg, rgba(139,92,246,0.1) 0%, rgba(37,99,235,0.06) 100%)",
						border: "1px solid rgba(139,92,246,0.18)",
						boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
					}}
				>
					<div
						className="absolute top-0 left-[20%] right-[20%] h-px z-10"
						style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.7), transparent)" }}
					/>

					<div className="p-6 flex flex-col items-center justify-center z-10 flex-1">
						<Globe className="w-8 h-8 text-violet-400" />
						<p className="text-xl sm:text-2xl font-semibold leading-snug text-center text-gray-100 mt-2">
							Full Control, Anytime
						</p>
					</div>

					<div className="relative h-[210px] flex-shrink-0">
						<div
							className="absolute top-0 left-0 right-0 h-10 z-10 pointer-events-none"
							style={{ background: "linear-gradient(to bottom, rgba(8,8,24,0.3), transparent)" }}
						/>
						<img
							src={wallpaper3}
							alt="Domain management dashboard"
							className="w-full h-full object-cover object-top"
							style={{ filter: "brightness(0.55) saturate(0.75)" }}
						/>
					</div>
				</div>
			</div>

			{/* ── ROW 2 ── */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

				{/* Card 4 — Fast setup */}
				<div
					className="rounded-2xl p-6 flex flex-col items-center justify-center min-h-[200px] lg:min-h-[260px] relative"
					style={{
						background: "linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
						border: "1px solid rgba(255,255,255,0.08)",
						boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
					}}
				>
					<div
						className="absolute top-0 left-[20%] right-[20%] h-px"
						style={{ background: "linear-gradient(90deg, transparent, rgba(45,212,191,0.35), transparent)" }}
					/>
					<div className="flex items-center mb-4">
						<div
							className="w-8 h-5 rounded-full"
							style={{ border: "2.5px solid rgba(45,212,191,0.7)", boxShadow: "0 0 8px rgba(45,212,191,0.2)" }}
						/>
						<div
							className="w-8 h-5 rounded-full -ml-3"
							style={{ border: "2.5px solid rgba(45,212,191,0.7)", boxShadow: "0 0 8px rgba(45,212,191,0.2)" }}
						/>
					</div>
					<p className="text-xl sm:text-2xl font-semibold leading-snug text-center text-gray-100">
						Quick and Easy Setup
					</p>
				</div>

				{/* Card 5 — Business tools */}
				<div
					className="lg:col-span-2 rounded-2xl overflow-hidden flex flex-col sm:flex-row items-stretch min-h-[260px] relative"
					style={{
						background: "linear-gradient(145deg, rgba(139,92,246,0.08) 0%, rgba(37,99,235,0.05) 100%)",
						border: "1px solid rgba(139,92,246,0.15)",
						boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
					}}
				>
					<div
						className="absolute top-0 left-[10%] right-[10%] h-px z-10"
						style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.55), transparent)" }}
					/>

					<div className="p-6 flex flex-col justify-end w-full sm:w-[240px] sm:flex-shrink-0">
						<p className="text-xl sm:text-2xl font-semibold leading-snug text-gray-100">
							Everything your business needs — websites, email, marketing tools, and more
						</p>
					</div>

					<div className="flex-1 flex flex-col justify-end min-w-0 relative min-h-[200px] sm:min-h-0">
						<div
							className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none hidden sm:block"
							style={{ background: "linear-gradient(to right, rgba(8,8,24,0.85), transparent)" }}
						/>
						<div
							className="absolute top-0 left-0 right-0 h-16 z-10 pointer-events-none sm:hidden"
							style={{ background: "linear-gradient(to bottom, rgba(8,8,24,0.85), transparent)" }}
						/>
						<div className="flex-1 overflow-hidden min-h-[200px] sm:min-h-full">
							<img
								src={wallpaper2}
								alt="Business tools dashboard"
								className="w-full h-full object-cover object-top"
								style={{ filter: "brightness(0.65) saturate(0.8)" }}
							/>
						</div>
					</div>
				</div>

			</div>
		</section>
	);
}